import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import CloudIcon from '@mui/icons-material/Cloud';
import AirIcon from '@mui/icons-material/Air';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Link } from 'react-router-dom';
import { config } from '../config';

const DEFAULT_LOCATION = {
  name: 'Northumberland, England',
  country: 'United Kingdom',
  latitude: 55.4157,
  longitude: -1.7066
};

const MODES = [
  { value: 'astrophotography', label: 'Astrophotography' },
  { value: 'stargazing', label: 'Stargazing' },
  { value: 'moon', label: 'Moon viewing' },
  { value: 'planetary', label: 'Planetary' }
];

const bandColours = {
  astronomical: '#72f0ff',
  'astro-twilight': '#6f8dff',
  nautical: '#a36cff',
  civil: '#f5974f',
  unknown: '#777'
};

const scoreColour = (score = 0) => {
  if (score >= 78) return '#76ffb2';
  if (score >= 62) return '#d8ff72';
  if (score >= 45) return '#ffd36f';
  if (score >= 28) return '#ff946f';
  return '#ff6b8a';
};

const formatPct = (value) => typeof value === 'number' ? `${Math.round(value)}%` : '—';
const formatNumber = (value, suffix = '') => typeof value === 'number' ? `${Math.round(value)}${suffix}` : '—';
const formatUpdated = (iso, timezone) => {
  if (!iso) return 'Updated recently';
  try {
    return `Updated ${new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: timezone || 'Europe/London' }).format(new Date(iso))}`;
  } catch (err) {
    return 'Updated recently';
  }
};

function Metric({ icon, label, value, hint }) {
  return (
    <Paper sx={{ p: 1.25, height: '100%', background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.025))', border: '1px solid rgba(255,255,255,0.08)' }}>
      <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 0.6 }}>
        {icon}
        <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1.2, textTransform: 'uppercase' }}>{label}</Typography>
      </Stack>
      <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', md: '1.05rem' } }}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Paper>
  );
}

function NightBar({ row }) {
  const score = row.score || 0;
  const absoluteDarkness = row.darkness?.darknessPct || 0;
  const adjustedDarkness = row.darkness?.adjustedPct ?? absoluteDarkness;
  const seasonalHint = row.darkness?.seasonalLabel || row.darkness?.seasonalContext || row.darkness?.label || 'Unknown';
  const cloud = typeof row.cloudCoverPct === 'number' ? row.cloudCoverPct : 0;
  const clearSky = Math.max(0, 100 - cloud);
  return (
    <Paper sx={{ p: 1.5, background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Box sx={{ minWidth: 64 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.98rem' }}>{row.time}</Typography>
          <Typography variant="caption" color="text.secondary">{seasonalHint}</Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
            <Typography variant="caption" sx={{ width: 68, color: 'text.secondary' }}>Seasonal</Typography>
            <Box title="Season-adjusted darkness: higher is better for this date/location" sx={{ flex: 1, height: 8, borderRadius: 999, overflow: 'hidden', background: 'rgba(255,255,255,0.08)' }}>
              <Box sx={{ width: `${adjustedDarkness}%`, height: '100%', background: bandColours[row.darkness?.band] || bandColours.unknown }} />
            </Box>
            <Typography variant="caption" sx={{ width: 40, textAlign: 'right' }}>{adjustedDarkness}%</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" sx={{ width: 68, color: 'text.secondary' }}>Forecast</Typography>
            <Box title="Forecast clear sky: higher is better (opposite of forecast cloud cover). This is public forecast model data, not private/local observing equipment data." sx={{ flex: 1, height: 8, borderRadius: 999, overflow: 'hidden', background: 'rgba(255,255,255,0.08)' }}>
              <Box sx={{ width: `${clearSky}%`, height: '100%', background: 'linear-gradient(90deg, #76ffb2, #d8ff72)' }} />
            </Box>
            <Typography variant="caption" sx={{ width: 40, textAlign: 'right' }}>{clearSky}%</Typography>
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} sx={{ minWidth: { sm: 260 }, flexWrap: 'wrap' }}>
          <Chip size="small" label={`${score} ${row.scoreLabel}`} sx={{ bgcolor: `${scoreColour(score)}22`, color: scoreColour(score), border: `1px solid ${scoreColour(score)}66`, fontWeight: 800 }} />
          <Chip size="small" label={`Rain ${formatPct(row.precipitationProbabilityPct)}`} />
          <Chip size="small" label={`Gust ${formatNumber(row.windGustMph, ' mph')}`} />
        </Stack>
      </Stack>
    </Paper>
  );
}

function MiniSkyGraph({ rows = [] }) {
  const points = rows.map((row, index) => ({
    index,
    time: row.time,
    darkness: row.darkness?.darknessPct || 0,
    clear: Math.max(0, 100 - (row.cloudCoverPct || 0)),
    score: row.score || 0
  }));
  if (!points.length) return null;
  const width = 360;
  const height = 142;
  const padX = 22;
  const padY = 16;
  const xFor = (index) => points.length === 1 ? width / 2 : padX + (index / (points.length - 1)) * (width - padX * 2);
  const yFor = (value) => padY + (1 - Math.max(0, Math.min(100, value)) / 100) * (height - padY * 2);
  const line = (key) => points.map(p => `${xFor(p.index)},${yFor(p[key])}`).join(' ');
  const best = points.reduce((a, b) => b.score > a.score ? b : a, points[0]);
  return (
    <Paper sx={{ p: 1.2, background: 'linear-gradient(180deg, rgba(8,9,16,0.78), rgba(8,9,16,0.35))', border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1, textTransform: 'uppercase' }}>Night sky curve</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>Darkness curve with forecast clear-sky overlay</Typography>
        </Box>
        <Chip size="small" label={`Peak ${best.time}`} sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.68rem' } }} />
      </Stack>
      <Box sx={{ position: 'relative', width: '100%', height: 142 }}>
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Selected night darkness and clear-sky curve" style={{ width: '100%', height: '100%', display: 'block' }}>
          <defs>
            <linearGradient id="darknessGlow" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#a36cff" />
              <stop offset="55%" stopColor="#72f0ff" />
              <stop offset="100%" stopColor="#d8ff72" />
            </linearGradient>
          </defs>
          {[25, 50, 75].map(v => <line key={v} x1={padX} x2={width - padX} y1={yFor(v)} y2={yFor(v)} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />)}
          <line x1={padX} x2={width - padX} y1={yFor(0)} y2={yFor(0)} stroke="rgba(255,255,255,0.16)" strokeWidth="1" />
          <polyline points={line('clear')} fill="none" stroke="#8cffaa" strokeWidth="2" strokeDasharray="5 5" opacity="0.72" />
          <polyline points={line('darkness')} fill="none" stroke="url(#darknessGlow)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          {points.map(p => (
            <circle key={p.time} cx={xFor(p.index)} cy={yFor(p.darkness)} r={p.index === best.index ? 4.8 : 3.2} fill={p.index === best.index ? '#ffd36f' : '#c7b7ff'} stroke="#080910" strokeWidth="1.5" />
          ))}
          {points.filter((_, i) => i % 3 === 0 || i === points.length - 1).map(p => (
            <text key={`t-${p.time}`} x={xFor(p.index)} y={height - 2} textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="11">{p.time}</text>
          ))}
          <text x="4" y={yFor(100) + 4} fill="rgba(255,255,255,0.55)" fontSize="10">100%</text>
          <text x="8" y={yFor(0) - 3} fill="rgba(255,255,255,0.55)" fontSize="10">0%</text>
        </svg>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 0.7, flexWrap: 'wrap', gap: 0.7 }}>
        <Chip size="small" label="Darkness" sx={{ bgcolor: 'rgba(163,108,255,0.16)', color: '#c7b7ff', height: 22 }} />
        <Chip size="small" label="Forecast clear" sx={{ bgcolor: 'rgba(118,255,178,0.12)', color: '#8cffaa', height: 22 }} />
        <Chip size="small" label="Best score marker" sx={{ bgcolor: 'rgba(255,211,111,0.12)', color: '#ffd36f', height: 22 }} />
      </Stack>
    </Paper>
  );
}

function NightSelector({ nights = [], selectedIndex, onSelect }) {
  if (!nights.length) return null;
  return (
    <Paper sx={{ p: 1, background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1, textTransform: 'uppercase', display: 'block', mb: 0.8 }}>Next 5 nights</Typography>
      <Stack spacing={0.75}>
        {nights.map((night, idx) => {
          const active = idx === selectedIndex;
          const best = night.bestWindow;
          return (
            <Button
              key={night.date || idx}
              onClick={() => onSelect(idx)}
              fullWidth
              variant={active ? 'contained' : 'outlined'}
              sx={{
                justifyContent: 'space-between',
                py: 0.7,
                px: 1,
                borderColor: active ? 'rgba(245,151,79,0.65)' : 'rgba(255,255,255,0.16)',
                bgcolor: active ? 'rgba(245,151,79,0.22)' : 'rgba(255,255,255,0.02)',
                color: active ? 'primary.light' : 'text.primary',
                '&:hover': { bgcolor: active ? 'rgba(245,151,79,0.28)' : 'rgba(255,255,255,0.06)' }
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 850, lineHeight: 1.15 }}>{night.label || night.date}</Typography>
                <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', lineHeight: 1.15 }}>{night.date}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: scoreColour(best?.score || 0), lineHeight: 1.15 }}>{best?.time || '—'}</Typography>
                <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', lineHeight: 1.15 }}>{best ? `${best.score}/100` : 'No data'}</Typography>
              </Box>
            </Button>
          );
        })}
      </Stack>
    </Paper>
  );
}

export default function NightChart() {
  const [location, setLocation] = useState(DEFAULT_LOCATION);
  const [mode, setMode] = useState('astrophotography');
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedNightIndex, setSelectedNightIndex] = useState(0);
  const [error, setError] = useState('');

  const fetchChart = useCallback(async (nextLocation = location, nextMode = mode) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        lat: String(nextLocation.latitude),
        lon: String(nextLocation.longitude),
        name: nextLocation.name || nextLocation.label || 'Selected location',
        country: nextLocation.country || '',
        mode: nextMode
      });
      const response = await fetch(`${config.endpoints.nightChart}?${params.toString()}`);
      if (!response.ok) throw new Error(`Night chart returned HTTP ${response.status}`);
      const data = await response.json();
      setChart(data);
      setSelectedNightIndex(0);
    } catch (err) {
      setError(err.message || 'Unable to load the night chart right now.');
    } finally {
      setLoading(false);
    }
  }, [location, mode]);

  useEffect(() => {
    fetchChart(DEFAULT_LOCATION, 'astrophotography');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const refreshMs = 60 * 60 * 1000;
    const interval = setInterval(() => {
      fetchChart(location, mode);
    }, refreshMs);
    return () => clearInterval(interval);
  }, [fetchChart, location, mode]);

  const displayNameForLocation = useCallback((item) => {
    if (!item) return 'Selected location';
    const baseName = item.name || item.label || 'Selected location';
    if (item.admin1 && !String(baseName).toLowerCase().includes(String(item.admin1).toLowerCase())) {
      return `${baseName}, ${item.admin1}`;
    }
    return baseName;
  }, []);

  const applyLocation = useCallback((item, options = {}) => {
    if (!item) return;
    const next = {
      ...item,
      name: displayNameForLocation(item),
      country: item.country || '',
      label: item.label || [displayNameForLocation(item), item.country].filter(Boolean).join(', ')
    };
    setLocation(next);
    setQuery(next.label);
    if (!options.keepResults) setResults([]);
    fetchChart(next, mode);
  }, [displayNameForLocation, fetchChart, mode]);

  const searchLocations = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setError('');
    try {
      const response = await fetch(`${config.endpoints.nightChart}/geocode?q=${encodeURIComponent(query.trim())}`);
      if (!response.ok) throw new Error('Location search failed');
      const data = await response.json();
      const found = data.results || [];
      if (!found.length) {
        setResults([]);
        setError('No matching location found. Try a nearby town, city or full postcode.');
        return;
      }
      applyLocation(found[0], { keepResults: true });
      setResults(found.slice(1));
    } catch (err) {
      setError(err.message || 'Unable to search locations.');
    } finally {
      setSearching(false);
    }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Your browser does not support geolocation.');
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition((pos) => {
      const next = {
        name: 'Your current location',
        country: '',
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude
      };
      setLocation(next);
      fetchChart(next, mode);
    }, (err) => {
      setLoading(false);
      const msg = (err && err.message) ? String(err.message) : '';
      setError(msg.toLowerCase().includes('permissions policy')
        ? 'Location sharing was blocked by the site/browser policy. I have enabled it on the site; if your browser still blocks it, type your town or postcode instead.'
        : 'Location sharing was not allowed or timed out. Type your town or postcode instead — the chart will still work.');
    }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 10 * 60 * 1000 });
  };

  const nights = chart?.nights || [];
  const activeNight = nights[selectedNightIndex] || chart || null;
  const best = activeNight?.bestWindow;
  const rows = activeNight?.rows || [];
  const aggregates = activeNight?.aggregates || chart?.aggregates || {};
  const moon = activeNight?.moon || chart?.moon;
  const title = chart?.location ? [chart.location.name, chart.location.country].filter(Boolean).join(', ') : 'Northumberland, England';
  const modeLabel = useMemo(() => MODES.find(m => m.value === mode)?.label || 'Astrophotography', [mode]);

  return (
    <Box sx={{ pb: 5, background: 'radial-gradient(circle at top, rgba(108,54,145,0.25), transparent 42%), #080910' }}>
      <Box sx={{ py: { xs: 2.1, md: 2.5 }, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Container maxWidth="xl">
          <Stack spacing={1.05} sx={{ maxWidth: 780 }}>
            <Chip icon={<AutoAwesomeIcon />} label="Public Night Chart" sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(245,151,79,0.14)', color: 'primary.light', border: '1px solid rgba(245,151,79,0.35)' }} />
            <Typography variant="h2" component="h1" sx={{ fontWeight: 850, letterSpacing: '-0.035em', fontSize: { xs: '1.28rem', md: '1.7rem' } }}>
              Plan tonight under the sky.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.65, fontSize: { xs: '0.78rem', md: '0.84rem' } }}>
              Choose a location and observing mode to see darkness, cloud, rain, wind and moon impact from evening through dawn.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="contained" startIcon={<MyLocationIcon />} onClick={useCurrentLocation} sx={{ bgcolor: 'primary.light', color: '#160920', fontWeight: 800, fontSize: '0.82rem', py: 0.75, '&:hover': { bgcolor: '#ffb06f' } }}>
                Use my location
              </Button>
              <Button component={Link} to="/gallery" variant="outlined" startIcon={<CameraAltIcon />} sx={{ borderColor: 'rgba(255,255,255,0.28)', fontSize: '0.82rem', py: 0.75 }}>
                Browse captures
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: -3 }}>
        <Paper sx={{ p: { xs: 1.6, md: 2.1 }, mb: 2.3, background: 'rgba(15,17,28,0.92)', border: '1px solid rgba(255,255,255,0.10)', backdropFilter: 'blur(16px)' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Search town, city or postcode"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') searchLocations(); }}
                placeholder="e.g. Harrogate, Edinburgh, NE1 1AA"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button fullWidth variant="outlined" startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />} onClick={searchLocations} disabled={searching} sx={{ height: 44, fontSize: '0.78rem' }}>
                Search
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField select fullWidth label="Observing mode" value={mode} onChange={(e) => { setMode(e.target.value); fetchChart(location, e.target.value); }}>
                {MODES.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" onClick={() => fetchChart(location, mode)} sx={{ height: 44, fontSize: '0.78rem' }}>
                Refresh chart
              </Button>
            </Grid>
          </Grid>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.6, flexWrap: 'wrap', gap: 1 }}>
            <Chip
              icon={<LocationOnIcon />}
              label={`Active forecast: ${title}`}
              sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,255,255,0.12)', fontWeight: 700 }}
            />
          </Stack>
          {results.length > 0 && (
            <Box sx={{ mt: 1.4 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.8 }}>
                Other matches — tap one to switch the forecast location
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {results.map((result) => (
                  <Chip
                    key={`${result.id}-${result.latitude}-${result.longitude}`}
                    icon={<LocationOnIcon />}
                    label={result.label}
                    clickable
                    onClick={() => applyLocation(result)}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Paper>

        {error && <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>}

        {loading && !chart ? (
          <Paper sx={{ p: 5, textAlign: 'center', background: 'rgba(255,255,255,0.04)' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Building tonight's chart…</Typography>
          </Paper>
        ) : chart && (
          <Grid container spacing={2.2}>
            <Grid item xs={12} lg={4}>
              <Paper sx={{ p: { xs: 1.35, md: 1.55 }, background: 'linear-gradient(150deg, rgba(108,54,145,0.35), rgba(12,14,24,0.92))', border: '1px solid rgba(245,151,79,0.22)' }}>
                <Stack spacing={1.35}>
                  <Chip icon={<LocationOnIcon />} label={title} sx={{ alignSelf: 'flex-start' }} />
                  <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.2rem' } }}>{activeNight?.label || 'Tonight'} for {modeLabel}</Typography>
                  {best && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Best season-adjusted window</Typography>
                      <Typography variant="h3" sx={{ color: scoreColour(best.score), fontWeight: 850, fontSize: { xs: '1.45rem', md: '1.75rem' } }}>{best.time}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{best.score}/100 · {best.scoreLabel}</Typography>
                    </Box>
                  )}
                  <Typography color="text.secondary" sx={{ lineHeight: 1.45, fontSize: '0.8rem' }}>{activeNight?.recommendation || chart.recommendation}</Typography>
                  {best && (
                    <Paper sx={{ p: 1.2, background: 'rgba(255,255,255,0.055)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1, textTransform: 'uppercase' }}>Why this time</Typography>
                      <Stack spacing={0.55} sx={{ mt: 0.7 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>Seasonal darkness: {formatPct(best.darkness?.adjustedPct ?? best.darkness?.darknessPct)} · actual {formatPct(best.darkness?.darknessPct)} · {best.darkness?.seasonalContext || best.darkness?.label || 'Unknown'}</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>Forecast cloud: {formatPct(best.cloudCoverPct)} · rain risk: {formatPct(best.precipitationProbabilityPct)}</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>Max gust: {formatNumber(best.windGustMph, ' mph')}</Typography>
                      </Stack>
                    </Paper>
                  )}
                  <NightSelector nights={nights} selectedIndex={selectedNightIndex} onSelect={setSelectedNightIndex} />
                  <MiniSkyGraph rows={rows} />
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Chip icon={<NightsStayIcon />} label={`${moon?.illuminationPct ?? '—'}% ${moon?.phaseName || 'Moon'}`} />
                    <Chip label={chart.location?.timezone || 'Local forecast'} />
                    <Chip label={formatUpdated(chart.generatedAt, chart.location?.timezone)} variant="outlined" />
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={8}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={2.4}><Metric icon={<NightsStayIcon sx={{ color: '#c7b7ff' }} />} label="Moon phase" value={moon?.phaseName || 'Moon'} hint={`${moon?.illuminationPct ?? '—'}% illuminated`} /></Grid>
                <Grid item xs={6} md={2.4}><Metric icon={<NightsStayIcon sx={{ color: '#72f0ff' }} />} label="Seasonal dark" value={formatPct(aggregates?.darknessAdjustedMaxPct ?? aggregates?.darknessMaxPct)} hint={`Actual peak ${formatPct(aggregates?.darknessMaxPct)}`} /></Grid>
                <Grid item xs={6} md={2.4}><Metric icon={<CloudIcon sx={{ color: '#d8ff72' }} />} label="Forecast cloud" value={formatPct(aggregates?.cloudCoverAvgPct)} hint="Model average; lower is better" /></Grid>
                <Grid item xs={6} md={2.4}><Metric icon={<WaterDropIcon sx={{ color: '#7bb8ff' }} />} label="Rain risk" value={formatPct(aggregates?.precipitationProbabilityMaxPct)} hint="Peak hourly risk" /></Grid>
                <Grid item xs={6} md={2.4}><Metric icon={<AirIcon sx={{ color: '#ffd36f' }} />} label="Max gust" value={formatNumber(aggregates?.windGustMaxMph, ' mph')} hint="Tracking stability" /></Grid>
              </Grid>

              {loading && <LinearProgress sx={{ mb: 2 }} />}
              <Stack spacing={1.2}>
                <Stack direction="row" spacing={1.5} sx={{ px: 1, display: { xs: 'none', sm: 'flex' } }}>
                  <Typography variant="caption" sx={{ width: 64, color: 'text.secondary' }}>Time</Typography>
                  <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>Bars: Seasonal = date-adjusted darkness, Forecast = model clear sky</Typography>
                  <Typography variant="caption" sx={{ width: 260, color: 'text.secondary' }}>Score / rain / wind</Typography>
                </Stack>
                {rows.map(row => <NightBar key={row.isoLocal} row={row} />)}
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 1.8, background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <VisibilityIcon sx={{ color: 'primary.light' }} />
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>How to read this</Typography>
                      <Typography variant="body2" color="text.secondary">Each hour has two labelled bars: Seasonal shows date-adjusted darkness; Forecast shows the model clear-sky percentage after cloud cover. Higher is better for both. Weather inputs use cross-referenced public forecast sources only; private/local observing equipment is not used for public forecasts.</Typography>
                    </Box>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">Cross-referenced weather sources only: {chart.source || (chart.providers?.join(' + ') || 'Open-Meteo best-match + UK Met Office model via Open-Meteo + MET Norway')}. Cloud spread max: {formatPct(aggregates?.cloudCoverSpreadMaxPct)}.</Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
}
